import { ChevronLeft } from 'lucide-react'
import type { ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { mayGoBack } from '../lib/two-step-payment'
import { Button } from './Button'
import {
  type CardStart,
  CheckoutElements,
  type useCheckoutConfirmation,
} from './CheckoutElements'
import { Card } from './index'
import './payment-flow.css'

type Flow = {
  /** Where the card step is drawn: the place the answers were. */
  slot: HTMLElement | null
  /** The card step says it is showing, and that it has gone. */
  paying: (on: boolean) => void
}

const FlowContext = createContext<Flow | null>(null)

/**
 * A public payment, in its two steps: the answers, then the card in their place.
 *
 * The answers stay MOUNTED while the card is showing, and are only hidden.
 * That is what Back keeping every answer rests on: a question holds its answer
 * in its own component — a membership's household, a pledge's plan — and one
 * unmounted to make room for the card would come back empty. Hidden, nothing
 * is lost, however deep in the page the question was.
 *
 * So the card step does not render where it is written. It is written beside
 * the checkout it belongs to, which may be several components down, and draws
 * itself here, in the place of the answers — one place for every page, whichever
 * of its questions opened the checkout.
 */
export function PaymentFlow({
  children,
  hidden = false,
}: {
  children: ReactNode
  /**
   * The whole flow is held out of sight, answers and card both, while the
   * page does not know whether the last payment went through: offering to
   * pay again then is offering to pay twice. Kept mounted, so every answer
   * is there if it did not.
   */
  hidden?: boolean
}) {
  const [slot, setSlot] = useState<HTMLDivElement | null>(null)
  const [showing, setShowing] = useState(0)
  /* The last thing focused among the answers — the commit that opened the
     checkout — is where Back returns the keyboard, so the donor is put back
     where they left rather than at the top of the document. Followed as focus
     moves rather than read when the card opens, because the commit is
     disabled while the checkout opens and a disabled button has already given
     its focus up by then. */
  const lastFocused = useRef<HTMLElement | null>(null)
  const wasShowing = useRef(false)
  const paying = useCallback((on: boolean) => {
    setShowing((n) => Math.max(0, n + (on ? 1 : -1)))
  }, [])
  useEffect(() => {
    const now = showing > 0
    if (wasShowing.current && !now && lastFocused.current?.isConnected) {
      lastFocused.current.focus()
    }
    wasShowing.current = now
  }, [showing])
  const flow = useMemo(() => ({ slot, paying }), [slot, paying])

  return (
    <FlowContext.Provider value={flow}>
      <div
        className="payment-flow__answers"
        hidden={hidden || showing > 0}
        onFocus={(e) => {
          /* The card step is a React child of this box though it is drawn
             elsewhere, so its focus bubbles here too; only focus that lands
             among the answers themselves is somewhere to come back to. */
          if (
            e.target instanceof HTMLElement &&
            e.currentTarget.contains(e.target)
          ) {
            lastFocused.current = e.target
          }
        }}
      >
        {children}
      </div>
      <div className="payment-flow__card" ref={setSlot} hidden={hidden} />
    </FlowContext.Provider>
  )
}

/**
 * Answers kept mounted while hidden: a branch of the flow set aside while a
 * question above it is asked again, so what was answered inside it is there if
 * the donor picks it again. It draws no box, so its questions take the flow's
 * spacing as if it were not there.
 */
export function HeldAnswers({
  held,
  children,
}: {
  held: boolean
  children: ReactNode
}) {
  return (
    <div className="payment-flow__held" hidden={held}>
      {children}
    </div>
  )
}

/**
 * The second step: a strip saying what is being paid and the way back, then
 * the card, then Pay.
 *
 * The strip is the only summary. Every answer is already folded on the step it
 * replaced, and Back is the one way to any of them: it keeps them all and lets
 * the checkout go, because a checkout is locked to what it was opened for.
 */
export function PaymentStep({
  orgId,
  card,
  what,
  who,
  amount,
  amountNote,
  payLabel,
  fine = 'Secure payment by Stripe. Your card details never touch our servers.',
  confirmation,
  onBack,
  onCardChange,
  onConfirmingChange,
}: {
  orgId: string
  /** What the card fields are drawn from, as the server prepared it. */
  card: CardStart
  /** What is being paid for, as the donor reads it back. */
  what: string
  /** Who is paying, where the page asked. */
  who?: string
  /** The figure the checkout will charge, formatted. */
  amount: string
  /** What the figure includes or how often it recurs. */
  amountNote?: string
  /** The commit. It names what it charges, because this button IS the payment. */
  payLabel: string
  fine?: string
  confirmation: ReturnType<typeof useCheckoutConfirmation>
  /** Discard the checkout and show the answers again, every one intact. */
  onBack: () => void
  onCardChange?: () => void
  onConfirmingChange?: (confirming: boolean) => void
}) {
  const flow = useContext(FlowContext)
  const regionRef = useRef<HTMLElement>(null)
  const paying = flow?.paying
  /* Before paint, so the answers and the card are never on screen together. */
  useLayoutEffect(() => {
    if (!paying) return
    paying(true)
    return () => paying(false)
  }, [paying])
  /* The step replaces what had focus, so it takes focus itself — the same
     reason a question does when it replaces the one before it. */
  const placed = flow ? flow.slot !== null : true
  useEffect(() => {
    if (placed) regionRef.current?.focus()
  }, [placed])
  /* Pay is disabled while the bank answers, and a disabled button gives its
     focus up to the document. A payment that fails hands it back, so the
     keyboard is on the thing to retry rather than at the top of the page. */
  const payRef = useRef<HTMLButtonElement>(null)
  const wasPending = useRef(false)
  const { pending, completed } = confirmation
  useEffect(() => {
    const settled = wasPending.current && !pending
    wasPending.current = pending
    if (!settled || completed) return
    const at = document.activeElement
    if (!at || at === document.body) payRef.current?.focus()
  }, [pending, completed])

  const body = (
    <section
      ref={regionRef}
      className="payment-step"
      aria-label={`Payment for ${what}`}
      tabIndex={-1}
    >
      <Card tone="filled">
        <div className="payment-step__strip">
          <Button
            variant="secondary"
            aria-label="Back to your answers"
            disabled={!mayGoBack(confirmation)}
            onClick={onBack}
          >
            <ChevronLeft className="btn__icon" aria-hidden />
            <span aria-hidden>Back</span>
          </Button>
          <span className="payment-step__what">
            <span className="payment-step__for" dir="auto">
              {what}
            </span>
            {who ? <span className="payment-step__who">{who}</span> : null}
          </span>
          <span className="payment-step__total">
            <span className="payment-step__amount tabular">{amount}</span>
            {amountNote ? (
              <span className="payment-step__note">{amountNote}</span>
            ) : null}
          </span>
        </div>
      </Card>
      <CheckoutElements
        orgId={orgId}
        card={card}
        onConfirmAvailable={confirmation.onConfirmAvailable}
        onComplete={confirmation.onComplete}
        trackConfirm={confirmation.track}
        onCardChange={onCardChange}
        onConfirmingChange={onConfirmingChange}
      />
      {/* A failed payment leaves this enabled again: the checkout stands, the
          card field keeps what was typed, and paying is the thing to retry. */}
      <Button
        ref={payRef}
        block
        onClick={() => void confirmation.confirm?.()}
        disabled={
          confirmation.pending ||
          confirmation.completed ||
          !confirmation.confirm
        }
      >
        {payLabel}
      </Button>
      <p className="payment-step__fine">{fine}</p>
    </section>
  )

  /* Outside a flow there is no place of the answers to take, so the step is
     drawn where it is written. */
  if (!flow) return body
  return flow.slot ? createPortal(body, flow.slot) : null
}
